import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { settingsAPI } from '../utils/api';
import CommandPalette from './CommandPalette';
import logo from '../logo.png';
import { resolveMediaUrl } from '../utils/mediaUrl';

/* ── Icon Components ─────────────────────────── */
const MenuToggleIcon = ({ isOpen, color = 'currentColor' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" style={{ transition: 'transform 0.25s' }}>
    <line x1="4" y1="6" x2="20" y2="6" style={{ transition: 'all 0.3s', transform: isOpen ? 'rotate(45deg) translate(5px, -2px)' : 'none', transformOrigin: 'center' }} />
    <line x1="4" y1="12" x2="20" y2="12" style={{ transition: 'all 0.2s', opacity: isOpen ? 0 : 1 }} />
    <line x1="4" y1="18" x2="20" y2="18" style={{ transition: 'all 0.3s', transform: isOpen ? 'rotate(-45deg) translate(5px, 2px)' : 'none', transformOrigin: 'center' }} />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

/* ── Constants ────────────────────────────────── */
const SIDEBAR_W = 252;
const SIDEBAR_W_MOBILE = 280;
const TOPBAR_H = 56;
const MOBILE_BREAKPOINT = 1024;

const Layout = ({ children }) => {
  const { user, logout, userType, loginRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode, toggleDark, colorKey, setColor, color, themes } = useTheme();
  const accent = color?.swatch || '#3B82F6';

  const [open, setOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= MOBILE_BREAKPOINT);
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  /* ── Resize handler ── */
  useEffect(() => {
    const onResize = () => {
      const isMob = window.innerWidth < MOBILE_BREAKPOINT;
      setMobile(isMob);
      if (!isMob && !open) setOpen(true);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  /* ── Close sidebar on mobile route change ── */
  useEffect(() => { if (mobile) setOpen(false); }, [location.pathname, mobile]);

  /* ── Load shop logo from settings ── */
  const [shopLogoUrl, setShopLogoUrl] = useState(null);
  useEffect(() => {
    if (userType !== 'admin') {
      settingsAPI.get().then(r => {
        if (r.data?.logo_url) setShopLogoUrl(resolveMediaUrl(r.data.logo_url));
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Keyboard shortcuts ── */
  useEffect(() => {
    const onKey = e => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const inInput = tag === 'input' || tag === 'textarea' || tag === 'select' || document.activeElement?.isContentEditable;

      // Command palette: Ctrl/Cmd+K — always works
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowCommandPalette(s => !s);
        return;
      }

      if (e.key === '?' && !inInput) { e.preventDefault(); setShowShortcuts(s => !s); return; }
      // Esc handled below after function keys

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        const active = document.activeElement;
        const form = active?.closest?.('form') || document.querySelector('form');
        if (form) {
          e.preventDefault();
          form.requestSubmit();
          return;
        }
      }

      // Function keys — always work (even in inputs)
      const fkeyActions = {
        F1: 'new-invoice', F2: 'purchase-bill', F3: 'add-product', F4: 'add-customer',
        F5: 'add-supplier', F6: 'new-quotation', F7: 'new-payment', F8: 'send-message', F9: 'print',
      };
      const fkeyRoutes = {
        F1: '/billing', F2: '/billing', F3: '/products', F4: '/parties',
        F5: '/parties', F6: '/quotations', F7: '/payments', F8: '/marketing',
      };
      const fkeyQueryExtra = { F2: '&mode=purchase' };
      const fkeyActionType = { F8: 'compose' };
      if (fkeyActions[e.key]) {
        e.preventDefault();
        if (e.key === 'F9') { window.print(); }
        else {
          const route = fkeyRoutes[e.key];
          const currentPath = window.location.pathname;
          const actionType = fkeyActionType[e.key] || 'create';
          const extraQuery = fkeyQueryExtra[e.key] || '';
          if (route && currentPath !== route) {
            navigate(route + '?action=' + actionType + extraQuery + '&source=' + encodeURIComponent(currentPath));
            setTimeout(() => window.dispatchEvent(new CustomEvent('sv-open-modal', { detail: fkeyActions[e.key] })), 300);
          } else {
            window.dispatchEvent(new CustomEvent('sv-open-modal', { detail: fkeyActions[e.key] }));
          }
        }
        return;
      }

      // Esc: close overlays only (don't navigate — pages handle their own Escape)
      if (e.key === 'Escape') {
        if (showShortcuts || showCommandPalette) { setShowShortcuts(false); setShowCommandPalette(false); }
        return;
      }

      if (inInput) return;
      if (e.altKey) {
        const altRoutes = { d: '/dashboard', a: '/parties', p: '/products', u: '/stock-audit', b: '/billing', q: '/quotations', k: '/marketing', r: '/reports', y: '/payments', n: '/delivery-notes', m: '/store-management', s: '/settings' };
        const route = altRoutes[e.key.toLowerCase()];
        if (route) { e.preventDefault(); navigate(route); }
      }
    };

    // Listen for custom events from CommandPalette
    const onShowShortcuts = () => setShowShortcuts(true);
    const onToggleDark = () => toggleDark();

    window.addEventListener('keydown', onKey);
    window.addEventListener('sv-show-shortcuts', onShowShortcuts);
    window.addEventListener('sv-toggle-dark', onToggleDark);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('sv-show-shortcuts', onShowShortcuts);
      window.removeEventListener('sv-toggle-dark', onToggleDark);
    };
  }, [navigate, toggleDark, showShortcuts, showCommandPalette]);

  const toggleSidebar = useCallback(() => setOpen(s => !s), []);

  /* ── Navigation config ── */
  const clientNav = useMemo(() => [
    { name: 'Dashboard', href: '/dashboard', icon: '📊', key: 'D' },
    { name: 'Parties', href: '/parties', icon: '👥', key: 'A' },
    { name: 'Products', href: '/products', icon: '📦', key: 'P' },
    { name: 'Stock Audit', href: '/stock-audit', icon: '📋', key: 'U' },
    { name: 'Billing', href: '/billing', icon: '💰', key: 'B' },
    { name: 'Quotations', href: '/quotations', icon: '📋', key: 'Q' },
    { name: 'Marketing', href: '/marketing', icon: '📣', key: 'K' },
    { name: 'Reports', href: '/reports', icon: '📈', key: 'R' },
    { name: 'Payments', href: '/payments', icon: '💳', key: 'Y' },
    { name: 'Delivery Notes', href: '/delivery-notes', icon: '🚚', key: 'N' },
    { name: 'Store Management', href: '/store-management', icon: '🏬', key: 'M' },
    { name: 'Settings', href: '/settings', icon: '⚙️', key: 'S' },
  ], []);
  const adminNav = useMemo(() => [
    { name: 'Dashboard', href: '/dashboard', icon: '📊', key: 'D' },
    { name: 'Shop Owners', href: '/admin', icon: '🏪', key: 'O' },
    { name: 'Settings', href: '/settings', icon: '⚙️', key: 'S' },
  ], []);
  const navigation = userType === 'admin' ? adminNav : clientNav;

  /* Whether sidebar actually pushes content (only on desktop when open) */
  const sidebarPushes = !mobile && open;

  return (
    <div className="app-shell" style={{
      background: darkMode ? '#0F172A' : '#F1F5F9',
      minHeight: '100dvh',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      overflow: 'hidden',   /* prevent horizontal scroll on outer shell */
      width: '100%',
      maxWidth: '100%',
    }}>

      {/* ── Mobile overlay backdrop ── */}
      {mobile && open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 40,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            transition: 'opacity 0.28s',
          }}
        />
      )}

      {/* ── Sidebar ── */}
      <aside className="app-sidebar" style={{
        position: 'fixed', top: 0, left: 0, zIndex: 50,
        width: mobile ? SIDEBAR_W_MOBILE : SIDEBAR_W,
        maxWidth: mobile ? '85vw' : SIDEBAR_W,
        height: '100dvh',
        display: 'flex', flexDirection: 'column',
        background: darkMode
          ? 'linear-gradient(180deg, #1E293B 0%, #0F172A 100%)'
          : 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        borderRight: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`,
        boxShadow: open ? '4px 0 32px rgba(0,0,0,0.15)' : 'none',
        transform: open ? 'translateX(0)' : `translateX(-105%)`,
        transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1), box-shadow 0.3s',
        willChange: 'transform',
      }}>
        {/* Sidebar header */}
        <div style={{
          height: TOPBAR_H, padding: '0 16px',
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
            <img src={logo} alt="Smart Vyapari" style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover', background: '#fff', padding: 2, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#fff', fontSize: 15, fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Smart Vyapari</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '0.03em' }}>Simplify Your Business</div>
            </div>
          </div>
          {/* Close button — clean pill with chevron */}
          <button
            onClick={() => setOpen(false)}
            aria-label="Close sidebar"
            style={{
              flexShrink: 0, width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
              cursor: 'pointer', color: '#fff',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            <ChevronLeftIcon />
          </button>
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '10px 8px', overflowY: 'auto', overflowX: 'hidden' }}>
          {navigation.map(item => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                style={{
                  display: 'flex', alignItems: 'center', padding: '10px 12px', borderRadius: 10,
                  marginBottom: 2, textDecoration: 'none', fontSize: 14, fontWeight: isActive ? 700 : 500,
                  background: isActive ? `${accent}18` : 'transparent',
                  color: isActive ? accent : (darkMode ? '#94A3B8' : '#64748B'),
                  borderLeft: `3px solid ${isActive ? accent : 'transparent'}`,
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                <span style={{ fontSize: 17, marginRight: 10, flexShrink: 0 }}>{item.icon}</span>
                <span style={{ flex: 1 }}>{item.name}</span>
                {item.key && !mobile && (
                  <kbd style={{ fontSize: 9, fontFamily: 'monospace', fontWeight: 600, padding: '1px 5px', borderRadius: 4, background: darkMode ? '#0F172A' : '#F1F5F9', color: darkMode ? '#475569' : '#94A3B8', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, marginLeft: 4 }}>Alt+{item.key}</kbd>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar footer */}
        <div style={{ padding: '10px 12px 14px', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, flexShrink: 0 }}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: darkMode ? '#F1F5F9' : '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {loginRole === 'staff' ? (user?.name || user?.email) : (user?.owner_name || user?.email)}
            </div>
            <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8' }}>
              {userType === 'admin' ? '🔑 Super Admin' : loginRole === 'staff' ? '👤 Staff' : '🛍️ Shop Owner'}
            </div>
          </div>
          <div style={{ paddingTop: 8, borderTop: `1px solid ${darkMode ? '#334155' : '#F1F5F9'}` }}>
            <p style={{ textAlign: 'center', fontSize: 9, color: darkMode ? '#475569' : '#CBD5E1', margin: 0, lineHeight: 1.5 }}>
              Made with ♥ by <strong style={{ color: darkMode ? '#64748B' : '#94A3B8' }}>@Gaurav_Komarewar</strong>
            </p>
          </div>
        </div>
      </aside>

      {/* ── Main Content Area ── */}
      <div style={{
        marginLeft: sidebarPushes ? SIDEBAR_W : 0,
        width: sidebarPushes ? `calc(100% - ${SIDEBAR_W}px)` : '100%',
        maxWidth: sidebarPushes ? `calc(100% - ${SIDEBAR_W}px)` : '100%',
        transition: 'margin-left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)',
        minHeight: '100dvh',
        display: 'flex', flexDirection: 'column',
        overflowX: 'hidden',
      }}>
        {/* ── Topbar ── */}
        <header className="app-topbar" style={{
          position: 'sticky', top: 0, zIndex: 20,
          height: TOPBAR_H, flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '0 16px',
          background: darkMode ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${darkMode ? '#1E293B' : '#E2E8F0'}`,
        }}>
          {/* Animated menu toggle — hidden on desktop when sidebar is open (sidebar has its own close button) */}
          {!(sidebarPushes) && (
          <button
            onClick={toggleSidebar}
            aria-label={open ? 'Close menu' : 'Open menu'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, borderRadius: 10,
              border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`,
              background: open
                ? (darkMode ? `${accent}22` : `${accent}0d`)
                : (darkMode ? '#1E293B' : '#F8FAFC'),
              cursor: 'pointer',
              color: open ? accent : (darkMode ? '#94A3B8' : '#64748B'),
              flexShrink: 0, transition: 'all 0.25s',
            }}
          >
            <MenuToggleIcon isOpen={open} color={open ? accent : (darkMode ? '#94A3B8' : '#64748B')} />
          </button>
          )}

          {/* Center: Logo + Shop Name */}
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, pointerEvents: 'none', minWidth: 0,
          }}>
            <img
              src={shopLogoUrl || logo}
              alt=""
              onError={(e) => { e.currentTarget.src = logo; }}
              style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover', background: accent, padding: 2, flexShrink: 0 }}
            />
            <span style={{
              fontSize: 14, fontWeight: 800, color: accent,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {user?.shop_name || 'Smart Vyapari'}
            </span>
          </div>

          {/* Right controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {/* Command Palette button */}
            <button onClick={() => setShowCommandPalette(true)} title="Command Palette (Ctrl+K)"
              style={{
                height: 34, borderRadius: 8, padding: '0 10px',
                border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`,
                background: darkMode ? '#1E293B' : '#F8FAFC',
                cursor: 'pointer', fontSize: 12, fontWeight: 600,
                color: darkMode ? '#64748B' : '#94A3B8',
                display: 'flex', alignItems: 'center', gap: 6,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.color = accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? '#334155' : '#E2E8F0'; e.currentTarget.style.color = darkMode ? '#64748B' : '#94A3B8'; }}
            >
              <span style={{ fontSize: 13 }}>🔍</span>
              {!mobile && <span>Search...</span>}
              {!mobile && <kbd style={{
                fontSize: 10, fontFamily: 'monospace', fontWeight: 700,
                padding: '1px 5px', borderRadius: 4,
                background: darkMode ? '#0F172A' : '#F1F5F9',
                border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`,
                marginLeft: 4,
              }}>⌘K</kbd>}
            </button>

            {!mobile && (
              <div style={{ textAlign: 'right', marginRight: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: darkMode ? '#F1F5F9' : '#334155', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
                  {loginRole === 'staff' ? (user?.name || user?.email) : (user?.owner_name || user?.email)}
                </div>
                <div style={{ fontSize: 10, color: darkMode ? '#64748B' : '#94A3B8' }}>
                  {loginRole === 'staff' ? '👤 Staff' : userType === 'admin' ? '🔑 Admin' : '🛍️ Owner'}
                </div>
              </div>
            )}
            <button onClick={() => navigate('/settings')} title="Settings"
              style={{
                width: 38, height: 38, borderRadius: 9,
                border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`,
                background: darkMode ? '#1E293B' : '#F8FAFC',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: darkMode ? '#94A3B8' : '#64748B', transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = darkMode ? '#334155' : '#E2E8F0'; e.currentTarget.style.color = accent; }}
              onMouseLeave={e => { e.currentTarget.style.background = darkMode ? '#1E293B' : '#F8FAFC'; e.currentTarget.style.color = darkMode ? '#94A3B8' : '#64748B'; }}
            ><SettingsIcon /></button>
            <button onClick={logout} title="Logout"
              style={{
                width: 38, height: 38, borderRadius: 9,
                border: `1px solid ${darkMode ? '#7F1D1D' : '#FECACA'}`,
                background: darkMode ? '#1E293B' : '#FEF2F2',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#EF4444', transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = darkMode ? '#3B1C1C' : '#FEE2E2'; }}
              onMouseLeave={e => { e.currentTarget.style.background = darkMode ? '#1E293B' : '#FEF2F2'; }}
            ><LogoutIcon /></button>
          </div>
        </header>

        {/* ── Page content ── */}
        <main className="app-main page-enter" key={location.pathname} style={{
          flex: 1,
          padding: mobile ? '12px' : '24px 28px',
          color: darkMode ? '#F1F5F9' : '#0F172A',
          width: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}>
          {children}
        </main>

        {/* ── Page Footer ── */}
        <footer style={{
          padding: '12px 20px',
          borderTop: `1px solid ${darkMode ? '#1E293B' : '#E2E8F0'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: darkMode ? 'rgba(15,23,42,0.6)' : 'rgba(248,250,252,0.8)',
          flexShrink: 0,
        }}>
          <img src={logo} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'cover' }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: darkMode ? '#475569' : '#94A3B8' }}>Smart Vyapari</span>
          <span style={{ fontSize: 10, color: darkMode ? '#334155' : '#CBD5E1' }}>•</span>
          <span style={{ fontSize: 10, color: darkMode ? '#334155' : '#CBD5E1' }}>Simplify Your Business</span>
        </footer>
      </div>

      {/* ── Keyboard Shortcuts Overlay ── */}
      {showShortcuts && (
        <div onClick={() => setShowShortcuts(false)} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: darkMode ? '#1E293B' : '#fff', borderRadius: 16, padding: '28px 32px', maxWidth: 'min(480px, calc(100vw - 40px))', width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.4)', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ color: darkMode ? '#F1F5F9' : '#0F172A', margin: 0, fontSize: 17, fontWeight: 800 }}>Keyboard Shortcuts</h3>
              <button onClick={() => setShowShortcuts(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8', lineHeight: 1 }}>×</button>
            </div>
            {[
              { group: 'Navigation (Alt + Letter)', items: [
                { key: 'Alt + D', desc: 'Dashboard' }, { key: 'Alt + A', desc: 'Parties' },
                { key: 'Alt + P', desc: 'Products' }, { key: 'Alt + B', desc: 'Billing' },
                { key: 'Alt + Q', desc: 'Quotations' }, { key: 'Alt + K', desc: 'Marketing' },
                { key: 'Alt + R', desc: 'Reports' }, { key: 'Alt + Y', desc: 'Payments' },
                { key: 'Alt + N', desc: 'Delivery Notes' }, { key: 'Alt + M', desc: 'Store Mgmt' },
                { key: 'Alt + S', desc: 'Settings' },
              ]},
              { group: 'Function Keys', items: [
                { key: 'F1', desc: 'Create Invoice' }, { key: 'F2', desc: 'Create Purchase Bill' },
                { key: 'F3', desc: 'Add Product' }, { key: 'F4', desc: 'Add Customer' },
                { key: 'F5', desc: 'Add Supplier' }, { key: 'F6', desc: 'New Quotation' },
                { key: 'F7', desc: 'Record Payment' }, { key: 'F8', desc: 'Send Message' },
                { key: 'F9', desc: 'Print Page' },
              ]},
              { group: 'Command Palette', items: [
                { key: 'Ctrl/⌘ + K', desc: 'Open command palette' },
                { key: '↑ ↓', desc: 'Navigate items' },
                { key: 'Enter', desc: 'Select item' },
              ]},
              { group: 'Global', items: [
                { key: '?', desc: 'Toggle this help' }, { key: 'Ctrl/⌘ + S', desc: 'Submit active form' },
                { key: 'Esc', desc: 'Close overlays / Go back' }, { key: '↑ ↓', desc: 'Navigate list rows' },
                { key: 'Tab', desc: 'Move between fields' },
              ]},
            ].map(({ group, items }) => (
              <div key={group} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: darkMode ? '#475569' : '#94A3B8', marginBottom: 6 }}>{group}</div>
                {items.map(({ key, desc }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${darkMode ? '#334155' : '#F1F5F9'}` }}>
                    <span style={{ fontSize: 13, color: darkMode ? '#94A3B8' : '#475569' }}>{desc}</span>
                    <kbd style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: darkMode ? '#0F172A' : '#F1F5F9', color: accent, border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>{key}</kbd>
                  </div>
                ))}
              </div>
            ))}
            <p style={{ fontSize: 11, color: darkMode ? '#475569' : '#CBD5E1', margin: '10px 0 0', textAlign: 'center' }}>
              Press <kbd style={{ fontSize: 10, fontFamily: 'monospace', padding: '1px 5px', borderRadius: 4, background: darkMode ? '#0F172A' : '#F1F5F9', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>?</kbd> anytime to toggle · <kbd style={{ fontSize: 10, fontFamily: 'monospace', padding: '1px 5px', borderRadius: 4, background: darkMode ? '#0F172A' : '#F1F5F9', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>Esc</kbd> to close
            </p>
          </div>
        </div>
      )}

      {/* ── Command Palette ── */}
      <CommandPalette open={showCommandPalette} onClose={() => setShowCommandPalette(false)} />
    </div>
  );
};

export default Layout;
